import React, { createContext, useContext } from 'react';
import { Box, Chip, TextField, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Switch from '../../../../common/Switch';
import { useMerchant } from '../../../../contexts/MerchantContext';
import { useGetMerchantQuery } from '../../../../store/modules/merchant/api';
import { useProvisioningPlatform } from '../../provisioning/useProvisioningPlatform';
import { useOnboardingDraft } from '../OnboardingDraftContext';
import {
  getOnboardingPlatform,
  SHOPIFY_PLATFORM_ID,
  SQUARESPACE_PLATFORM_ID,
  type YourStoreValidationContext,
} from '../platforms';
import { useSquarespaceStoreLink } from '../useSquarespaceStoreLink';

type YourStoreDraft = {
  platformId: string;
  shopDomain: string;
};

export type EcommercePlatformOption = {
  id: string;
  label: string;
  disabled?: boolean;
};

const ENABLED_ECOMMERCE_PLATFORM_IDS = new Set(['shopify', 'bigcommerce', 'squarespace']);

export const ECOMMERCE_PLATFORM_OPTIONS: readonly EcommercePlatformOption[] = [
  { id: 'shopify', label: 'Shopify' },
  { id: 'bigcommerce', label: 'BigCommerce' },
  { id: 'squarespace', label: 'Squarespace' },
  { id: 'adobe-magento', label: 'Adobe/Magento', disabled: true },
  { id: 'woocommerce', label: 'WooCommerce', disabled: true },
  { id: 'custom', label: 'Custom', disabled: true },
].map((option) => ({
  ...option,
  disabled: option.disabled ?? !ENABLED_ECOMMERCE_PLATFORM_IDS.has(option.id),
}));

export const DEFAULT_ECOMMERCE_PLATFORM_ID = SHOPIFY_PLATFORM_ID;

export type KnowledgeBaseFeature = {
  id: string;
  title: string;
  description: string;
  suggested?: boolean;
};

export const KNOWLEDGE_BASE_FEATURES: readonly KnowledgeBaseFeature[] = [
  {
    id: 'learn-return-policy',
    title: 'Learn return policy',
    description: "Enable this feature if you'd like the agent to learn your return policy.",
    suggested: true,
  },
  {
    id: 'handle-returns',
    title: 'Handle returns for shoppers',
    description:
      'Enable your agent to process returns, issue RMAs, and sync status with your CRM — not just answer policy questions.',
  },
  {
    id: 'shipping-delivery',
    title: 'Shipping & delivery',
    description: 'Rates, carriers, and delivery timelines.',
  },
  {
    id: 'faqs',
    title: 'FAQs',
    description: 'Common questions not covered by your help center.',
  },
];

export const KNOWLEDGE_BASE_SUGGESTION_LABEL = 'Suggested for Retail · Commerce Agent';

/** The error for the selected platform's store field, if any. */
export const getYourStoreError = (
  store: YourStoreDraft,
  context: YourStoreValidationContext = {},
): string | undefined =>
  getOnboardingPlatform(store.platformId).getStoreError(store.shopDomain, context);

export const isYourStoreStepValid = (
  store: YourStoreDraft,
  context?: YourStoreValidationContext,
): boolean => !getYourStoreError(store, context);

export const canAdvanceFromOnboardingStep = (
  stepId: string,
  draft: { store: YourStoreDraft },
  context?: YourStoreValidationContext,
): boolean => {
  if (stepId === 'your-store') {
    return isYourStoreStepValid(draft.store, context);
  }

  return true;
};

const YourStoreValidationContext = createContext(false);

type YourStoreValidationProviderProps = {
  showErrors: boolean;
  children: React.ReactNode;
};

export const YourStoreValidationProvider = ({
  showErrors,
  children,
}: YourStoreValidationProviderProps): JSX.Element => (
  <YourStoreValidationContext.Provider value={showErrors}>
    {children}
  </YourStoreValidationContext.Provider>
);

const useYourStoreShowErrors = (): boolean => useContext(YourStoreValidationContext);

const pillSx = (isSelected: boolean, isDisabled: boolean) => ({
  boxSizing: 'border-box' as const,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 44,
  px: 2,
  py: 1,
  border: '1px solid',
  borderColor: isSelected ? 'link.main' : 'border.main',
  borderRadius: '4px',
  backgroundColor: isSelected ? 'brand.light' : 'background.paper',
  color: 'text.primary',
  fontSize: 14,
  fontWeight: isSelected ? 600 : 400,
  lineHeight: '20px',
  whiteSpace: 'nowrap' as const,
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  opacity: isDisabled ? 0.55 : 1,
  font: 'inherit',
});

export const YourStoreStep = (): JSX.Element => {
  const { merchantId } = useMerchant();
  const { data: merchant } = useGetMerchantQuery(merchantId, { skip: !merchantId });
  const { link: squarespaceLink } = useSquarespaceStoreLink();
  const { platform: provisioningPlatform, isLoading: isPlatformLoading } =
    useProvisioningPlatform();
  const { draft, updateDraft } = useOnboardingDraft();
  const { platformId, shopDomain, knowledgeToggles } = draft.store;

  // shopDomain is reset on every platform change; Squarespace starts on the linked site.
  const squarespacePrefill = squarespaceLink?.linkedHost ?? '';
  const selectPlatform = React.useCallback(
    (nextPlatformId: string) => {
      updateDraft((prev) =>
        prev.store.platformId === nextPlatformId
          ? prev
          : {
              ...prev,
              store: {
                ...prev.store,
                platformId: nextPlatformId,
                shopDomain: nextPlatformId === SQUARESPACE_PLATFORM_ID ? squarespacePrefill : '',
              },
            },
      );
    },
    [squarespacePrefill, updateDraft],
  );

  // Preselects Squarespace once, and only over the untouched default.
  const isUntouchedDefault = platformId === DEFAULT_ECOMMERCE_PLATFORM_ID && !shopDomain;
  const hasResolvedPlatformRef = React.useRef(false);
  React.useEffect(() => {
    if (hasResolvedPlatformRef.current || isPlatformLoading) {
      return;
    }
    hasResolvedPlatformRef.current = true;
    if (provisioningPlatform === SQUARESPACE_PLATFORM_ID && isUntouchedDefault) {
      selectPlatform(SQUARESPACE_PLATFORM_ID);
    }
  }, [isPlatformLoading, provisioningPlatform, isUntouchedDefault, selectPlatform]);

  // Prefills once if Squarespace was selected before the link loaded.
  const hasPrefilledSquarespaceSiteRef = React.useRef(false);
  React.useEffect(() => {
    if (
      hasPrefilledSquarespaceSiteRef.current ||
      !squarespaceLink ||
      platformId !== SQUARESPACE_PLATFORM_ID
    ) {
      return;
    }
    hasPrefilledSquarespaceSiteRef.current = true;
    if (squarespacePrefill) {
      updateDraft((prev) =>
        prev.store.platformId !== SQUARESPACE_PLATFORM_ID || prev.store.shopDomain
          ? prev
          : { ...prev, store: { ...prev.store, shopDomain: squarespacePrefill } },
      );
    }
  }, [squarespaceLink, platformId, squarespacePrefill, updateDraft]);

  const showStoreErrors = useYourStoreShowErrors();
  const storeContext = {
    merchantExternalPlatformId: merchant?.externalPlatformId,
    squarespaceLink,
  };
  const { storeField, isStoreLocked } = getOnboardingPlatform(platformId);
  const isStoreFieldLocked = isStoreLocked(storeContext);
  const storeError = showStoreErrors
    ? getYourStoreError({ platformId, shopDomain }, storeContext)
    : undefined;

  return (
    <>
      <Box component="section" data-testid="ella-onboarding-platform-section" sx={{ mb: 4 }}>
        <Typography component="h3" variant="small" fontWeight="bold" sx={{ mb: 1 }}>
          Connect your store
        </Typography>
        <Typography variant="small" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Choose a ecommerce platform to sync your product catalog for shopper conversations.
        </Typography>

        <Box
          role="radiogroup"
          aria-label="Ecommerce platform"
          data-testid="ella-onboarding-ecommerce-platform"
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}
        >
          {ECOMMERCE_PLATFORM_OPTIONS.map((option) => {
            const isSelected = option.id === platformId;
            const isDisabled = Boolean(option.disabled);

            return (
              <Box
                key={option.id}
                component="button"
                type="button"
                role="radio"
                aria-checked={isSelected}
                disabled={isDisabled}
                data-testid={`ella-onboarding-ecommerce-platform-option-${option.id}`}
                onClick={() => {
                  if (!isDisabled) {
                    selectPlatform(option.id);
                  }
                }}
                sx={pillSx(isSelected, isDisabled)}
              >
                {option.label}
              </Box>
            );
          })}
        </Box>

        {storeField ? (
          <TextField
            // Remount per platform so read-only state never carries across.
            key={platformId}
            label={storeField.label}
            placeholder={isStoreFieldLocked ? undefined : storeField.placeholder}
            value={shopDomain}
            onChange={(event) => {
              updateDraft((prev) => ({
                ...prev,
                store: { ...prev.store, shopDomain: event.target.value },
              }));
            }}
            error={Boolean(storeError)}
            helperText={
              storeError ??
              (isStoreFieldLocked
                ? (storeField.lockedHelperText ?? storeField.helperText)
                : storeField.helperText)
            }
            InputProps={{ readOnly: isStoreFieldLocked }}
            InputLabelProps={isStoreFieldLocked ? { shrink: true } : undefined}
            fullWidth
            sx={{ mt: 2 }}
            data-testid={storeField.testId}
          />
        ) : null}
      </Box>

      <Box component="section" data-testid="ella-onboarding-knowledge-base">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography component="h3" variant="small" fontWeight="bold">
            Knowledge base
          </Typography>
          <Chip
            label={KNOWLEDGE_BASE_SUGGESTION_LABEL}
            size="small"
            sx={{
              height: 24,
              maxWidth: '100%',
              fontSize: 11,
              fontWeight: 600,
              bgcolor: 'success.light',
              color: 'success.dark',
              borderRadius: '4px',
            }}
          />
        </Box>

        <Typography variant="small" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Upload documents your agent can ingest alongside your catalog — return policies, shipping
          rules, FAQs and other shopper-facing content.
        </Typography>

        <Box
          data-testid="ella-onboarding-knowledge-info-banner"
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            px: 2,
            py: 1.5,
            mb: 2,
            borderRadius: '4px',
            bgcolor: 'success.light',
            border: '1px solid',
            borderColor: 'success.main',
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: 'link.main', mt: 0.25 }} aria-hidden />
          <Typography variant="small" sx={{ lineHeight: '18px' }}>
            Good news: you can always link other data and actions to provide deeper personalization
            later in the process.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {KNOWLEDGE_BASE_FEATURES.map((feature) => (
            <Box
              key={feature.id}
              data-testid={`ella-onboarding-knowledge-feature-${feature.id}`}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                px: 2,
                py: 1.75,
                border: '1px solid',
                borderColor: 'border.main',
                borderRadius: '4px',
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}
                >
                  <Typography variant="small" fontWeight="bold" sx={{ lineHeight: '20px' }}>
                    {feature.title}
                  </Typography>
                  {feature.suggested ? (
                    <Chip
                      label="Suggested"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: 'success.light',
                        color: 'success.dark',
                        borderRadius: '4px',
                      }}
                    />
                  ) : null}
                </Box>
                <Typography variant="small" color="text.secondary" sx={{ lineHeight: '18px' }}>
                  {feature.description}
                </Typography>
              </Box>

              <Switch
                isChecked={Boolean(knowledgeToggles[feature.id])}
                onToggle={() => undefined}
                isDisabled
                data-testid={`ella-onboarding-knowledge-toggle-${feature.id}`}
                sx={{ flexShrink: 0, mt: 0.25 }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};
